import { cn } from '@/lib/utils';
import { CheckCircle2, RefreshCw, X } from 'lucide-react';
import { useEffect, useState } from 'react';

const AUTO_DISMISS_MS = 3500;

export type OrderWsEvent =
  | {
      type: 'NEW_ORDER';
      deviceId: string;
      payload: {
        orderId: string;
        customerName: string;
        totalAmount: number;
        orderNumber: string;
      };
    }
  | {
      type: 'ORDER_UPDATED';
      deviceId: string;
      payload: {
        orderId: string;
        status: string;
        orderNumber?: string;
      };
    };

function eventKey(event: OrderWsEvent) {
  if (event.type === 'NEW_ORDER') {
    return `NEW_ORDER:${event.payload.orderId}`;
  }
  return `ORDER_UPDATED:${event.payload.orderId}:${event.payload.status}`;
}

export function OrderInlineAlert({
  event,
  count,
}: {
  event: OrderWsEvent | null;
  /** Total number of new orders received via WebSocket in this session */
  count: number;
}) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const key = event ? eventKey(event) : null;

  useEffect(() => {
    if (!key) {
      setVisible(false);
      setExiting(false);
      return;
    }

    setVisible(true);
    setExiting(false);

    const exitTimer = window.setTimeout(() => setExiting(true), AUTO_DISMISS_MS - 280);
    const hideTimer = window.setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, AUTO_DISMISS_MS);

    return () => {
      window.clearTimeout(exitTimer);
      window.clearTimeout(hideTimer);
    };
  }, [key]);

  if (!event || !visible) return null;

  const dismiss = () => {
    setExiting(true);
    window.setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, 200);
  };

  const countLabel =
    count > 1 ? (
      <span className="tabular-nums text-[10px] font-medium opacity-70">+{count - 1}</span>
    ) : null;

  if (event.type === 'NEW_ORDER') {
    const { orderNumber, customerName, totalAmount } = event.payload;
    return (
      <div
        role="status"
        className={cn(
          'mb-2 flex items-center gap-1.5 rounded-md border border-emerald-200/80 bg-emerald-50/90 px-2 py-1 text-[11px] text-emerald-800 shadow-sm transition-all duration-200',
          exiting
            ? 'translate-y-[-2px] opacity-0'
            : 'translate-y-0 opacity-100 animate-in fade-in slide-in-from-top-1 duration-200',
        )}
      >
        <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" />
        <span className="font-semibold tabular-nums">#{orderNumber}</span>
        <span className="min-w-0 truncate text-emerald-700/80">{customerName}</span>
        <span className="shrink-0 tabular-nums text-emerald-700/70">
          ${totalAmount.toLocaleString()}
        </span>
        {countLabel}
        <button
          type="button"
          onClick={dismiss}
          className="ml-auto inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-emerald-700/50 hover:bg-emerald-100 hover:text-emerald-800"
          aria-label="Dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  const { status, orderNumber } = event.payload;
  return (
    <div
      role="status"
      className={cn(
        'mb-2 flex items-center gap-1.5 rounded-md border border-blue-200/80 bg-blue-50/90 px-2 py-1 text-[11px] text-blue-800 shadow-sm transition-all duration-200',
        exiting
          ? 'translate-y-[-2px] opacity-0'
          : 'translate-y-0 opacity-100 animate-in fade-in slide-in-from-top-1 duration-200',
      )}
    >
      <RefreshCw className="h-3 w-3 shrink-0 text-blue-600" />
      <span className="font-semibold tabular-nums">
        {orderNumber ? `#${orderNumber}` : 'Order'}
      </span>
      <span className="truncate uppercase tracking-wide text-blue-700/80">{status}</span>
      {countLabel}
      <button
        type="button"
        onClick={dismiss}
        className="ml-auto inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-blue-700/50 hover:bg-blue-100 hover:text-blue-800"
        aria-label="Dismiss"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
